
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/auth';
import { UserProfile } from '@/types/user.types';
import { calculateFeatureAccess } from '@/utils/user/featureAccess';
import type { Profile } from '@/contexts/auth/types';

interface UserContextType {
  user: UserProfile | null;
  isLoading: boolean;
  featureAccess: any;
  refetchUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType>({
  user: null,
  isLoading: true,
  featureAccess: null,
  refetchUser: async () => {},
});

export const useUser = () => useContext(UserContext);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user: authUser, isLoading: authLoading, profile, hasRole, isAuthenticated } = useAuth();

  const featureAccess = userProfile ? calculateFeatureAccess(userProfile) : null;

  const mapAuthUserToProfile = useCallback((user: any, profile: Profile | null): UserProfile => {
    console.log('[UserContext] Mapping auth user to profile:', { user: user?.email, profile: profile?.username });
    
    // Calculate isAdmin status based on roles
    const isAdmin = hasRole('staff_admin') || hasRole('super_admin');
    
    // Calculate if the user is new (registered within the last 24 hours)
    const createdAt = profile?.created_at || user.created_at;
    const isNewUser = createdAt ? ((new Date().getTime() - new Date(createdAt).getTime()) < 86400000) : true;
    
    return {
      id: user.id,
      email: user.email,
      username: profile?.username || user.user_metadata?.username || null,
      name: profile?.username || user.user_metadata?.username || user.email?.split('@')[0] || 'User',
      role: profile?.role_text || 'free',
      avatarUrl: profile?.avatar_url || user.user_metadata?.avatar_url,
      createdAt: createdAt || new Date().toISOString(),
      updatedAt: profile?.updated_at || null,
      
      // Computed properties
      isLoggedIn: true,
      isAdmin: isAdmin,
      isNewUser: isNewUser,
      displayName: profile?.username || user.email?.split('@')[0] || 'User',
    };
  }, [hasRole]);

  // Update user profile when auth state changes
  useEffect(() => {
    console.log('[UserContext] Auth state effect triggered:', { 
      authLoading, 
      isAuthenticated, 
      hasAuthUser: !!authUser, 
      hasProfile: !!profile,
      authUserEmail: authUser?.email 
    });

    // Don't process while auth is still loading
    if (authLoading) {
      console.log('[UserContext] Auth still loading, waiting...');
      return;
    }

    // Handle authenticated user
    if (authUser && isAuthenticated) {
      console.log('[UserContext] User is authenticated, creating profile');
      const mappedProfile = mapAuthUserToProfile(authUser, profile);
      console.log('[UserContext] Setting user profile:', mappedProfile);
      setUserProfile(mappedProfile);
      setIsLoading(false);
    } else {
      // Handle unauthenticated user
      console.log('[UserContext] User not authenticated, clearing profile');
      setUserProfile(null);
      setIsLoading(false);
    }
  }, [authUser, profile, authLoading, isAuthenticated, mapAuthUserToProfile]);

  // Function to manually refresh user data
  const refetchUser = useCallback(async () => {
    if (!authUser || !isAuthenticated) {
      console.log('[UserContext] Cannot refetch: user not authenticated');
      return;
    }
    
    console.log('[UserContext] Refetching user data...');
    setIsLoading(true);
    
    try {
      const mappedProfile = mapAuthUserToProfile(authUser, profile);
      setUserProfile(mappedProfile);
      console.log('[UserContext] User data refreshed:', mappedProfile);
    } catch (error) {
      console.error("[UserContext] Error refreshing user data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [authUser, profile, isAuthenticated, mapAuthUserToProfile]);

  const contextValue = { 
    user: userProfile, 
    isLoading: isLoading, 
    featureAccess,
    refetchUser
  };

  console.log('[UserContext] Providing context:', {
    hasUser: !!contextValue.user,
    userEmail: contextValue.user?.email,
    isLoading: contextValue.isLoading,
    hasFeatureAccess: !!contextValue.featureAccess
  });

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};
